import Swal from 'sweetalert2';
import i18n from '@/i18n';  // SPEC-CHINA-ADMIN-LOCALE-CORE-001 — 옛 키 'locale' 대체
import enums from './enums';
import dayjs from "dayjs";
import 'dayjs/locale/ko'

const {YnEnum} = enums();

export function jwtDecoder(jwtCode) {
  resetWrongTokens();
  if (!jwtCode) {
    return '';
  }
  // base64url → base64 변환(- → +, _ → /) 후 UTF-8 안전 디코딩.
  // payload.name 등에 중국어가 있으면 base64url에 -/_ 문자가 생겨
  // atob가 InvalidCharacterError를 던지고 앱이 블랭크가 되던 버그 수정.
  // jwtRole()/jwtAccountType()과 동일 패턴. 실패 시 '' 반환(앱 중단 방지).
  try {
    const base64 = jwtCode.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
      ),
    );
    return payload['sub'] || '';
  } catch {
    return '';
  }
}

/**
 * JWT payload.role 디코딩 — OD-FE-2 확정 (SPEC-CHINA-ADMIN-HOSPITAL-001)
 *
 * 기존 jwtDecoder(sub만 디코드)를 변경하지 않고 신규 헬퍼로 추가.
 * 프론트 role은 UI 표시용이며 보안 경계가 아님 (EXC-6).
 * EcountEtl Dashboard.vue / RoleGuard.vue의 패턴 동일 적용.
 *
 * @returns {string} 'XINLAIAN' | 'MANUFACTURER' | 'DENTAL_LABORATORY' | ''
 */
export function jwtRole() {
  const token = localStorage.getItem('accessTokenManu');
  if (!token) return '';
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
      ),
    );
    const role = payload.role || payload.roles || '';
    const resolved = Array.isArray(role) ? (role[0] || '') : String(role);
    // [phase 3] 레거시 표기 'CERTILIFE'로의 정규화를 **제거했다**.
    // 종전에는 화면 분기 호환을 위해 XINLAIAN→CERTILIFE로 바꿔 돌려줬고, 그 결과
    // "JWT엔 XINLAIAN인데 코드에선 CERTILIFE"라는 이중 표기가 생겨 읽을 때마다 설명이 필요했다.
    // BE(jwt.strategy)도 같은 시점에 정규화를 제거했다. 이제 양쪽 모두 XINLAIAN 하나만 쓴다.
    return resolved;
  } catch {
    return '';
  }
}

/**
 * JWT payload.accountType 디코딩 — SPEC-CHINA-MFR-STAFF-ACCOUNT-001
 *
 * jwtRole()과 동일 패턴. auth store에 accountType이 저장되지 않으므로
 * localStorage JWT를 직접 디코딩한다.
 * 'STAFF' 반환 시 실무자 계정(마스터가 아님).
 *
 * @returns {string} 'STAFF' | '' (ADMIN 또는 미설정 시 빈 문자열)
 */
/**
 * 현재 사용자가 플랫폼 운영자(중국: XINLAIAN)인지 판별 — 신규 코드의 유일한 운영자 판정 지점.
 *
 * [정정 2026-08-02] 이 주석은 원래 "CERTILIFE는 jwtRole()이 정규화해 돌려주는 표기"라고
 * 적혀 있었다. phase 3에서 그 정규화를 제거했으므로 **거짓**이다.
 *
 * XINLAIAN이 중국 플랫폼 운영자의 정식 role이고, jwtRole()은 이제 JWT 값을 그대로 돌려준다.
 * 그래도 두 값을 다 받는 이유는 **정규화 제거 이전에 발급된 구 토큰** 때문이다.
 * 여기서 CERTILIFE를 빼면 아직 갱신 안 된 세션이 조용히 권한을 잃는다 — 토큰이 모두
 * 만료된 뒤에 좁히는 것이 맞다.
 *
 * @returns {boolean} 운영자 여부
 */
export function isOperator() {
  const role = jwtRole();
  // 'CERTILIFE'도 계속 받는다 — 정규화 제거 이전에 발급된 구 토큰이 남아 있을 수 있고,
  // 여기서 좁히면 그 세션이 조용히 권한을 잃는다. 신규 코드는 이 함수만 쓰고 리터럴은 쓰지 않는다.
  return role === 'XINLAIAN' || role === 'CERTILIFE';
}

export function jwtAccountType() {
  const token = localStorage.getItem('accessTokenManu');
  if (!token) return '';
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
      ),
    );
    return String(payload.accountType || '');
  } catch {
    return '';
  }
}

/**
 * JWT payload.username 디코드 — 자기 자신(self) 판별용.
 * SPEC-CHINA-MFR-STAFF-GUARD-001: 자기 행 비번초기화/강제 로그아웃 버튼 비활성.
 * jwtAccountType()과 동일 패턴 (base64url + UTF-8 안전 디코딩, 실패 시 '').
 *
 * @returns {string} 로그인 username | ''
 */
export function jwtUsername() {
  const token = localStorage.getItem('accessTokenManu');
  if (!token) return '';
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
      ),
    );
    return String(payload.username || '');
  } catch {
    return '';
  }
}

/**
 * JWT payload.deniedCaps 디코드 — capability 기반 UI 게이팅용.
 *
 * `deniedCaps` 는 **저장 형식이 deny-list(뺄셈)** 다. 다만 BE 판정은 2026-08-02부터
 * allow-list(`effectiveCapability`)로 바뀌었다 — "목록에 없으면 전체 허용"은 더 이상 BE 규약이 아니다.
 * jwtAccountType()과 동일 패턴(base64url + UTF-8 안전 디코딩, 실패 시 []).
 * @returns {string[]} 차단된 capability 배열
 */
export function jwtDeniedCaps() {
  const token = localStorage.getItem('accessTokenManu');
  if (!token) return [];
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
      ),
    );
    return Array.isArray(payload.deniedCaps) ? payload.deniedCaps : [];
  } catch {
    return [];
  }
}

/**
 * capability 보유 여부 — `deniedCaps` 에 없으면 true.
 *
 * [정정 2026-08-02] 원래 "BE hasCapability와 동일 규약"이라 적혀 있었다. `hasCapability()` 는
 * 폐기됐고 BE 판정은 `effectiveCapability()` 의 **allow-list**(`부여받았고 + 차감 안 됨`)다.
 *
 * **그래도 이 함수를 안 바꾸는 이유**: 이 화면들에 도달 가능한 role 은 `@Roles` 가
 * XINLAIAN·MANUFACTURER 로 좁혀 두었고, 둘 다 `ROLE_GRANTS` 에서 해당 cap 을 부여받는다.
 * 그래서 두 판정 결과가 갈리는 경우가 **현재 없다**(2026-08-02 실측).
 *
 * **깨지는 조건**: 새 role 에 `@Roles` 로 이 EP 를 열어주면서 `ROLE_GRANTS` 에 cap 을 안 주면,
 * FE 는 컨트롤을 그리는데 BE 가 403/필드 strip 을 낸다 — 죽은 버튼이 된다.
 * 열리는 방향이 아니라 닫히는 방향이라 보안 결함은 아니지만, 그때는 여기도 role 을 봐야 한다.
 *
 * 권위는 항상 BE(가드+필드 strip). 이 함수는 UI 렌더용 화장 계층이다.
 * @param {string} cap 예: 'certCard.cost'
 * @returns {boolean}
 */
export function jwtCan(cap) {
  return !jwtDeniedCaps().includes(cap);
}

export function resetWrongTokens() {
  const accessToken = localStorage.getItem('accessTokenManu');
  const refreshToken = localStorage.getItem('refreshTokenManu');
  if(accessToken === 'null' || accessToken === 'undefined') {
      localStorage.removeItem('accessTokenManu');    
  }
  if(refreshToken === 'null' || refreshToken === 'undefined') {
      localStorage.removeItem('refreshTokenManu');    
  }
}

export function changeBytesToMegabytes(bytes) {
  return bytes / 1024 / 1024;
}

export const formatCommaNumber = (value) => {
  if(value) return value.toLocaleString('en-US');
  else return value;
}

export const twoBtnHtmlDialog = async (title, htmlText, confirmBtnText) => {
  const response = await Swal.fire({
    title: title,
    html: htmlText,
    buttonsStyling: false,
    confirmButtonText: confirmBtnText ? confirmBtnText : '확인',
    cancelButtonText: '취소',
    showCancelButton: true,
    heightAuto: true,
    customClass: {
      confirmButton: 'btn btn-primary-confirm btn-hover swal-button-custom',
      cancelButton: 'btn btn-secondary-cancel btn-hover swal-button-custom',
    },
    reverseButtons: true,
  })
  if(response) {
    const isConfirmed = response.isConfirmed;
    return isConfirmed;
  } else {
    return false;
  }
}



// dialog 열기
export async function oneBtnDialog(title, text, needsRefresh) {
  await Swal.fire({
    title: title,
    text: text,
    buttonsStyling: false,
    confirmButtonText: i18n.global.locale.value === 'zh' ? '确认' : '확인',
    heightAuto: true,
    allowOutsideClick: false,
    customClass: {
      confirmButton: 'btn btn-primary btn-hover',
    },
  })
  .then(() => {
    if(needsRefresh) {
      setTimeout(() => {
        location.reload()
      }, 80);
    }
  });
}
export async function twoBtnDialog(title, text, confirmBtnText, cancelButtonText) {
  const response = await Swal.fire({
    title: title,
    text: text,
    buttonsStyling: false,
    confirmButtonText: confirmBtnText ? confirmBtnText : '확인',
    cancelButtonText: cancelButtonText ? cancelButtonText : '취소',
    showCancelButton: true,
    heightAuto: true,
    customClass: {
      confirmButton: ' btn-primary-confirm btn-hover swal-button-custom',
      cancelButton: ' btn-secondary-cancel btn-hover swal-button-custom',
    },
    reverseButtons: true,
  })
  if(response) {
    const isConfirmed = response.isConfirmed;
    return isConfirmed;
  } else {
    return false;
  }
}

export const oneBtnHtmlDialog = async (title, vueComponentElement, needsRefresh) => {
  await Swal.fire({
    title: title,
    html: vueComponentElement,
    buttonsStyling: false,
    showCancelButton: false,
    heightAuto: true,
    confirmButtonText: '확인',
    customClass: {
      confirmButton: 'btn btn-primary',
    },
    reverseButtons: true,
  })
  .then(() => {
    if(needsRefresh) location.reload();
  });
}

//삭제 dialog
export async function confirmDialog(title, text) {
  const response = await Swal.fire({
    title: title,
    text: text,
    buttonsStyling: false,
    confirmButtonText: '확인',
    cancelButtonText: '취소',
    showCancelButton: true,
    heightAuto: true,
    customClass: {
      confirmButton: 'btn btn-primary-confirm btn-hover',
      cancelButton: 'btn btn-secondary-cancel btn-hover',
    },
  })
  if(response) {
    const isConfirmed = response.isConfirmed;
    return isConfirmed;
  } else {
    return false;
  }
}
// 날짜만 보여주기(스트링 잘라서)
export function convertDate(dateString) {
  if(dateString) {
    return dateString.split('T')[0].replaceAll('-', '/');
  } else {
    return `-`;
  }
}

// 날짜와 시간 보여주기(스트링 잘라서)
export function convertDateTime(dateString) {
  if(dateString) {
    const date = dateString.split('T')[0].replaceAll('-', '/');
    const time = dateString.split('T')[1].split('.')[0];
    return `${date} ${time}`;
  } else {
    return `-`;
  }
}

export function phoneNumberFormatter(phoneNumber) {
  if (!phoneNumber) return '';
  else {
    const value = phoneNumber.replace(/[^0-9]/g, "");

    // Check if the length is 12 for special formatting
    if (value.length === 12) {
      return value.replace(/([0-9]{4})([0-9]{4})([0-9]{4})/, "$1-$2-$3");
    }

    // Existing formatting for other cases
    return value.replace(
      /(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/,
      "$1-$2-$3"
    );
  }
}

export function businessNumberFormatter(businessNumber) {
  if(!businessNumber) return '';
  const value = businessNumber.replace(/[^0-9]/g, "");

  if (value.length <= 3) {
    return value;
  } else if (value.length <= 5) {
    return value.replace(/(\d{3})(\d{0,2})/, '$1-$2');
  } else {
    return value.replace(/(\d{3})(\d{2})(\d{0,5})/, '$1-$2-$3');
  }
}

export function decodeUtfText(text) {
  const byteArray = Uint8Array.from(
      text,
      (c) => c.charCodeAt(0)
  );
  const decoder = new TextDecoder('utf-8');
  const decodedText = decoder.decode(byteArray);
  return decodedText;
}

export function splitNumberUntilZero(number) {
  let result = number;

  while (result % 10 === 0 && result !== 0) {
    result = Math.floor(result / 10);
  }

  return result;
}

export const  dateUtils = () => {
  return {
    unixTimeToYYYYMMDD(unixTime) {
      return dayjs(unixTime).format('YYYYMMDD');
    },
    YYYYMMDDtoUnixTime(dateString) {
      return dayjs(dateString, 'YYYYMMDD').valueOf();
    }
  }
}
export const logging = () => {
  return {
    exceptProd: (txt, obj) => {
      if (process.env.NODE_ENV !== "production") {
        console.log(txt, obj)
      }
    }
  }
}

export const deepCopy = (obj) => {
  if(!obj) return;
  return JSON.parse(JSON.stringify(obj));
}

export const convertYnToBool = (ynEnum) => {

  if(ynEnum === YnEnum.Y) return true;
  if(ynEnum === YnEnum.N) return false;
  return ynEnum;
}

export const boolToYn = (bool) => {
  if(bool === true) return YnEnum.Y;
  if(bool === false) return YnEnum.N;
  return bool;
}

export const getPrivilegeInfoByName = (categoryArray, categoryName, menuName) => {
  const obj = {
    create: false,
    read: false,
    update: false,
    delete: false,
  }
  if(!Array.isArray(categoryArray)) return obj;
  const category = categoryArray.find(category => category.name === categoryName);
  if(!category.menu || !Array.isArray(category.menu)) return obj;
  const menu = category ? category.menu.find(menu => menu.menuName === menuName) : null;
  if(!menu) return obj;
  if(menu.read && (menu.read === 'M' || menu.read === YnEnum.Y)) {
    obj.read = true;
  }
  if(menu.write && menu.write === YnEnum.Y) {
    obj.create = true;
  }
  if(menu.update && menu.update === YnEnum.Y) {
    obj.update = true;
  }
  if(menu.delete && menu.delete === YnEnum.Y) {
    obj.delete = true;
  }
  
  return obj;
}

export const getValueByTraitType = (attributes, targetValue) => {
  let value = '';
  if(attributes) {
    for(const item of attributes) {
      if(item.trait_type === targetValue) {
        value = item.value;
      }
    }
  }
  return value;
}